import React, { useState } from 'react';
import {
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
	CButton, CFormLabel, CFormSelect,
} from '@coreui/react';
import { toast } from 'react-toastify';
import AxiosInstance from 'src/components/Axios';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { STATUS_LETTER_OPTIONS } from 'src/constants/supportLetters';
import { patchCachedSession, patchCachedSupportHistory } from 'src/utils/supportRequestCache';
import SupportLettersModal from './SupportLettersModal';

/**
 * Каскадне модальне вікно статусів (П.9).
 * Відкривається після збереження листа адміна.
 * Послідовно пропонує вказати статус для листів юзера у ланцюжку
 * (від нового вгору до першого листа адміна — не включно).
 *
 * @param {boolean} open - чи відкрите вікно
 * @param {Array} userRequests - масив SupportRequest від юзера для оновлення ({id, user_email, sent_at, status})
 * @param {number|string} sessionId - ID сесії, в якій знаходяться ці листи (для патчу кешу)
 * @param {string} userEmail - email юзера (для патчу кешу історії)
 * @param {function} [onStatusApplied] - опціональний callback після кожного успішного PATCH: (requestId, newStatus) → void
 * @param {function} onClose - callback після завершення всіх кроків або скасування
 */
const SupportStatusCascadeModal = ({ open, userRequests = [], sessionId, userEmail, onStatusApplied, onClose }) => {
	const [step, setStep] = useState(0);
	const [statuses, setStatuses] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [viewModalOpen, setViewModalOpen] = useState(false);
	const [viewModalId, setViewModalId] = useState(null);

	const current = userRequests[step];

	const handleStatusChange = (e) => {
		setStatuses(prev => ({ ...prev, [current.id]: e.target.value }));
	};

	const handleNext = async () => {
		if (!current) return;
		const selectedStatus = statuses[current.id] || current.status || 'replied';
		setSubmitting(true);
		try {
			await AxiosInstance.patch(`/support-requests/${current.id}/status`, { status: selectedStatus });
			if (sessionId) {
				patchCachedSession(sessionId, s => ({
					...s,
					requests: (s.requests || []).map(r => r.id === current.id ? { ...r, status: selectedStatus } : r),
				}));
			}
			if (userEmail) {
				patchCachedSupportHistory(userEmail, cached => ({
					...cached,
					data: (cached.data || []).map(r => r.id === current.id ? { ...r, status: selectedStatus } : r),
				}));
			}
			onStatusApplied?.(current.id, selectedStatus);
		} catch {
			toast.error(`Помилка при оновленні статусу листа #${current.id}`);
		} finally {
			setSubmitting(false);
		}

		if (step + 1 >= userRequests.length) {
			setStep(0);
			setStatuses({});
			onClose();
		} else {
			setStep(prev => prev + 1);
		}
	};

	const handleCancel = () => {
		setStep(0);
		setStatuses({});
		onClose();
	};

	if (!current) return null;

	const currentStatus = statuses[current.id] || current.status || 'new';
	const shortMessage = current.message
		? (current.message.length > 100 ? current.message.slice(0, 100) + '…' : current.message)
		: '';

	return (
		<>
			<CModal visible={open} onClose={handleCancel} backdrop="static">
				<CModalHeader>
					<CModalTitle>
						Статус листа #{current.id} ({step + 1}/{userRequests.length})
						<button
							onClick={() => { setViewModalId(current.id); setViewModalOpen(true); }}
							title="Швидкий перегляд листа"
							disabled={submitting}
							style={{
								marginLeft: 8,
								border: '1px solid #6c757d',
								borderRadius: '0.2rem',
								padding: '0.15rem 0.4rem',
								background: 'none',
								cursor: submitting ? 'default' : 'pointer',
								fontSize: '0.85rem',
								verticalAlign: 'middle',
							}}
						>👁</button>
					</CModalTitle>
				</CModalHeader>
				<CModalBody>
					<FormSubmitOverlay submitting={submitting}>
						<p className="text-muted small mb-3">
							{current.user_email} · {current.sent_at || ''}
						</p>
						{shortMessage && (
							<p className="text-muted small mb-3" style={{ fontStyle: 'italic' }}>
								«{shortMessage}»
							</p>
						)}
						<CFormLabel>Вкажіть статус листа від юзера *</CFormLabel>
						<CFormSelect
							value={currentStatus}
							onChange={handleStatusChange}
						>
							{STATUS_LETTER_OPTIONS.map(o => (
								<option key={o.value} value={o.value}>{o.label}</option>
							))}
						</CFormSelect>
					</FormSubmitOverlay>
				</CModalBody>
				<CModalFooter>
					<CButton
						color="primary"
						onClick={handleNext}
						disabled={submitting}
						title={step + 1 < userRequests.length ? 'Зберегти і перейти до наступного листа' : 'Зберегти і завершити'}
					>
						{submitting
							? 'Збереження...'
							: (step + 1 < userRequests.length ? 'Зберегти і далі →' : 'Зберегти і завершити')
						}
					</CButton>
					<CButton color="secondary" variant="outline" onClick={handleCancel} disabled={submitting} title="Пропустити решту">
						Пропустити
					</CButton>
				</CModalFooter>
			</CModal>
			<SupportLettersModal
				open={viewModalOpen}
				requestId={viewModalId}
				onClose={() => setViewModalOpen(false)}
				readOnly
			/>
		</>
	);
};

export default SupportStatusCascadeModal;
